import { lazy } from 'react'
import { LoaderFunctionArgs, Navigate, ShouldRevalidateFunction } from 'react-router-dom'
import loaderCommon from '@/lib/helpers/loaderCommon.ts'
import { loaderSingleIssue } from '@/routes/projectStages/issue/actions.ts'
import { loaderStageIssuesByIssueType } from '@/routes/projectStages/issues/lib/actions/issuesActionsGetIssues.ts'
import { rootLoader } from '@/routes/root/lib/actions/rootLoader.ts'
import { loaderSingleReport } from '@/routes/projectStages/report/lib/actions.ts'

const ProjectSingleReport = lazy(
  () => import('@/routes/projectStages/report/view/ProjectSingleReport.tsx'),
)
const Dashboard = lazy(() => import('@/routes/projectStages/dashboard/view/Dashboard'))
const Reports = lazy(() => import('@/routes/projectStages/reports/view/list/Reports.tsx'))
const NewReportWorkspace = lazy(
  () => import('@/routes/projectStages/reports/view/new/NewReportWorkspace.tsx'),
)
const Archive = lazy(() => import('@/routes/projectStages/archive/view/Archive.tsx'))
const BgSync = lazy(() => import('@/routes/projectStages/bgSync/view/BgSync.tsx'))
const ProjectSingleIssue = lazy(
  () => import('@/routes/projectStages/issue/view/ProjectSingleIssue.tsx'),
)
const Issues = lazy(() => import('@/routes/projectStages/issues/view/Issues.tsx'))
const Notifications = lazy(
  () => import('@/routes/projectStages/notifications/view/Notifications.tsx'),
)
const ProjectsRoot = lazy(() => import('@/routes/projectStages/projectsRoot/ProjectsRoot.tsx'))
const RouteError = lazy(() => import('@/routes/ErrorPage.tsx'))

export type ProjectStageParamsT = {
  projectStageId: string
  issueId: string
  issueTypeId: string
  projectId: string
  reportId: string
}

export const PROJECT_STAGES_ROUTES = {
  path: 'project_stages',
  children: [
    {
      index: true,
      element: <Navigate to="/" replace />,
    },
    {
      path: ':projectStageId',
      element: <ProjectsRoot />,
      //
      loader: (params: LoaderFunctionArgs) => loaderCommon(rootLoader, params),
      shouldRevalidate: (({ currentParams, nextParams }) => {
        // only revalidate if the project stage ID has changed
        const shouldRevalidate = currentParams?.projectStageId !== nextParams?.projectStageId
        // console.log({ shouldRevalidate })

        return shouldRevalidate
      }) as ShouldRevalidateFunction,

      children: [
        {
          // Pathless layout route whose only job is to own an error boundary
          // for everything rendered inside a project stage.
          //
          // Before this, the app declared exactly ONE `errorElement` — on the
          // root route in `routes.tsx` — so it was the nearest boundary for
          // every screen, and any throw below it replaced the ENTIRE app
          // shell instead of just the failing content. That is what turned a
          // single bad notification row into a full-app crash (issue #361),
          // and what a production `insertBefore` commit crash on the issue
          // detail page did again.
          //
          // It bounds commit-phase failures too, not just render ones:
          // react-dom wraps `commitPlacement` in try/catch and hands the
          // error to `captureCommitPhaseError`, which walks up to the NEAREST
          // boundary — so placing one here is precisely what shrinks the
          // blast radius. Pathless keeps every child's URL, loader, index
          // route and `shouldRevalidate` behaviour untouched.
          errorElement: <RouteError />,
          children: [
            {
              index: true,
              element: <Navigate to="/" replace />,
            },
            {
              path: 'issue_types',
              element: <Navigate to="/" replace />,
            },
            {
              path: 'issue_types/:issueTypeId',
              element: <Navigate to="/" replace />,
            },
            {
              path: 'notifications',
              element: <Notifications />,
            },
            {
              path: 'sync-queue',
              element: <BgSync />,
            },
            {
              path: 'archive',
              element: <Archive />,
            },
            {
              path: 'reports',
              children: [
                {
                  path: 'new',
                  element: <NewReportWorkspace />,
                },
                {
                  path: ':reportId',
                  element: <ProjectSingleReport />,
                  loader: (params: LoaderFunctionArgs) => loaderCommon(loaderSingleReport, params),
                },
                {
                  index: true,
                  element: <Reports />,
                },
              ],
            },
            {
              path: 'dashboard',
              element: <Dashboard />,
            },
            {
              path: `issue_types/:issueTypeId/issues`,
              children: [
                {
                  index: true,
                  element: <Issues />,
                  loader: (params: LoaderFunctionArgs) =>
                    loaderCommon(loaderStageIssuesByIssueType, params),
                },
                {
                  path: ':issueId',
                  element: <ProjectSingleIssue />,
                  loader: (params: LoaderFunctionArgs) => loaderCommon(loaderSingleIssue, params),
                },
              ],
            },
          ],
        },
      ],
    },
  ],
}
